import { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Coins, Lightbulb, Loader2, Lock, Shield, Shirt, Sparkles } from 'lucide-react';
import { Dialog, DialogContent, DialogDescription, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { apiGet, apiPost } from '@/lib/api';

const KIND_ICON = {
  badge: Shield, outfit: Shirt, hint_pack: Lightbulb, legend_theme: Sparkles,
};
const KIND_LABEL = {
  badge: 'Badges', outfit: 'Buddy outfits', hint_pack: 'Hint packs', legend_theme: 'Legend scenes',
};

/** Coin Shop — what Veda Coins are for. */
export function CoinShopDialog({ open, onOpenChange }) {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState('');

  const load = useCallback(() => {
    apiGet('/shop/catalogue').then(setData).catch(() => setData(null));
  }, []);

  useEffect(() => { if (open) load(); }, [open, load]);

  const buy = async (sku) => {
    setBusy(sku);
    try {
      const res = await apiPost('/shop/purchase', { sku });
      toast.success(res.message);
      load();
    } catch (e) { toast.error(e?.message || 'Could not complete that'); }
    setBusy('');
  };

  const groups = ['legend_theme', 'outfit', 'badge', 'hint_pack'];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg rounded-3xl max-h-[90vh] overflow-y-auto" data-testid="coin-shop-dialog">
        <DialogTitle className="font-heading text-lg font-bold text-slate-900 flex items-center gap-2">
          <Coins className="w-4 h-4 text-amber-500" /> Coin Shop
        </DialogTitle>
        <DialogDescription className="text-xs text-slate-500 font-body">
          {data ? `${data.coins} Veda Coins · ${data.hint_credits} hints in your pocket` : 'Loading your wallet…'}
        </DialogDescription>

        {!data && <p className="py-6 text-center text-sm text-slate-400 font-body"><Loader2 className="w-4 h-4 animate-spin inline mr-2" />Loading…</p>}

        {data && (
          <div className="space-y-4">
            <p className="text-[11px] font-body text-slate-500 rounded-2xl border border-slate-200 p-2.5" data-testid="shop-discipline">
              {data.discipline.how}
            </p>
            {groups.map(kind => {
              const items = data.items.filter(i => i.kind === kind);
              if (!items.length) return null;
              const Icon = KIND_ICON[kind];
              return (
                <section key={kind} data-testid={`shop-group-${kind}`}>
                  <p className="text-[10px] font-heading uppercase tracking-widest text-slate-400 flex items-center gap-1.5">
                    <Icon className="w-3 h-3" /> {KIND_LABEL[kind]}
                  </p>
                  <div className="mt-1.5 space-y-1.5">
                    {items.map(item => (
                      <div key={item.sku} data-testid={`shop-item-${item.sku}`}
                        className="flex items-center gap-2 rounded-2xl border border-slate-200 p-2.5">
                        {item.image && (
                          <img src={item.image} alt={item.label} loading="lazy"
                            className="w-12 h-9 rounded-lg object-cover flex-shrink-0" />
                        )}
                        <div className="min-w-0 flex-1">
                          <p className="font-heading text-[12px] font-semibold text-slate-800">{item.label}</p>
                          <p className="text-[11px] text-slate-500 font-body truncate">{item.hint}</p>
                        </div>
                        <span className="text-[11px] font-heading text-amber-700 flex-shrink-0">{item.cost}</span>
                        {item.owned ? (
                          <span className="text-[10px] font-heading uppercase text-emerald-600 flex-shrink-0">owned</span>
                        ) : (
                          <Button size="sm" variant="outline" className="h-7 text-[10px] flex-shrink-0"
                            disabled={busy === item.sku || item.locked}
                            data-testid={`shop-buy-${item.sku}`} onClick={() => buy(item.sku)}>
                            {item.locked ? <Lock className="w-3 h-3 mr-1" /> : null}
                            {item.locked ? 'Locked' : busy === item.sku ? '…' : 'Get it'}
                          </Button>
                        )}
                      </div>
                    ))}
                  </div>
                </section>
              );
            })}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

export default CoinShopDialog;
